/**
 * Does a typed e-signature match the signer's account name?
 *
 * MIRRORS `src/policies/utils/names-match.ts` on the backend. The two are
 * separate builds with no shared package, so this is a deliberate duplicate —
 * keep the rules identical if either changes. The SERVER is the control; this
 * copy exists only so the user gets immediate feedback instead of a rejected
 * round-trip.
 *
 * Forgiving about presentation, strict about identity: case-insensitive,
 * whitespace-normalised, accent-insensitive.
 *
 * The accent rule is load-bearing in an Irish service — Sinéad, Seán and
 * Aoife Ní Bhriain are routinely typed without fadas on a phone keyboard, and
 * rejecting those would block a parent from signing their own name.
 */
const normalise = (value: string): string =>
  value
    .normalize('NFD')
    // Strip the combining diacritical marks NFD leaves behind (U+0300-U+036F).
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export function namesMatch(typed: string, accountName: string): boolean {
  const a = normalise(typed);
  const b = normalise(accountName);
  // Never let an empty account name turn this into a rubber stamp.
  if (!a || !b) return false;
  return a === b;
}
